/**
 * Remembered calendar preferences (vc.calendarPrefs): which day starts the week, which view a
 * fresh /calendar opens on and the remembered working hours. A dedicated small key rather than
 * devicePrefs fields: the values are calendar-surface state, not device state, and every field
 * is validated on load - anything unknown degrades to its default without rejecting the rest
 * of the document.
 *
 * The default view is a fallback only: the user's last chosen switcher view (vc.calendarView,
 * viewPref.ts) wins while it is still one of the five known views.
 */

import type { ViewId } from './CalendarPage';
import { CALENDAR_VIEW_STORAGE_KEY } from './viewPref';
import type { ViewPrefStorage } from './viewPref';

export const CALENDAR_PREFS_STORAGE_KEY = 'vc.calendarPrefs';

/** The day a calendar week starts on; FullCalendar's number comes from {@link firstDayOf}. */
export type StartOfWeek = 'monday' | 'saturday' | 'sunday';

export interface CalendarPrefs {
  readonly startOfWeek: StartOfWeek;
  readonly defaultView: ViewId;
  readonly workingHours: WorkingHoursPrefs;
}

/** The remembered working hours: which weekdays (FullCalendar numbers, 0 = Sunday) and which
 * `HH:MM` wall-clock range count as working time. */
export interface WorkingHoursPrefs {
  readonly start: string;
  readonly end: string;
  readonly days: readonly number[];
}

export const DEFAULT_WORKING_HOURS: WorkingHoursPrefs = {
  start: '09:00',
  end: '18:00',
  days: [1, 2, 3, 4, 5],
};

export const DEFAULT_CALENDAR_PREFS: CalendarPrefs = {
  startOfWeek: 'monday',
  defaultView: 'dayGridMonth',
  workingHours: DEFAULT_WORKING_HOURS,
};

/** FullCalendar's single-range `businessHours` option (daysOfWeek entries are 0 = Sunday). */
export interface BusinessHoursRange {
  readonly daysOfWeek: number[];
  readonly startTime: string;
  readonly endTime: string;
}

/** The minimal storage surface the preferences need (structural - the KeyValueStorage the
 * settings page uses and the DOM Storage both satisfy it). */
export type CalendarPrefsStorage = ViewPrefStorage;

const KNOWN_STARTS: readonly StartOfWeek[] = ['monday', 'saturday', 'sunday'];

/** The five views of the calendar switcher; the same list viewPref.ts guards vc.calendarView
 * with (kept local: viewPref.ts stays untouched). */
const KNOWN_VIEWS: readonly ViewId[] = [
  'multiMonthYear',
  'dayGridMonth',
  'timeGridWeek',
  'workWeek',
  'timeGridDay',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Guards a raw stored value as one of the five switcher views; CalendarPage runs the same
 * check over vc.calendarView before the preferences' default view applies. */
export function isKnownView(value: unknown): value is ViewId {
  return KNOWN_VIEWS.some((view) => view === value);
}

function readStartOfWeek(value: unknown): StartOfWeek {
  const match = KNOWN_STARTS.find((start) => start === value);
  return match ?? DEFAULT_CALENDAR_PREFS.startOfWeek;
}

function readDefaultView(value: unknown): ViewId {
  return isKnownView(value) ? value : DEFAULT_CALENDAR_PREFS.defaultView;
}

/** Fixed-width `HH:MM` wall clock, 00:00..23:59 ('24:00' is deliberately rejected: FullCalendar
 * business-hour times must stay inside the day). Fixed width makes plain string comparison a
 * correct time ordering. */
const WORK_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

function isWorkingTime(value: string): boolean {
  return WORK_TIME_PATTERN.test(value);
}

/** The single source of truth for the working-range rules, shared by the loader below and the
 * Settings card: both times must be `HH:MM` and the end must be strictly after the start. */
export function isValidWorkingRange(start: string, end: string): boolean {
  return isWorkingTime(start) && isWorkingTime(end) && start < end;
}

/** Only arrays of unique integers 0..6 count; anything else (non-array, garbage entries,
 * duplicates, empty) degrades to the Mon-Fri default as a whole. */
function readWorkingDays(value: unknown): readonly number[] {
  if (!Array.isArray(value) || value.length === 0) {
    return DEFAULT_WORKING_HOURS.days;
  }
  const days: number[] = [];
  for (const entry of value) {
    if (
      typeof entry !== 'number' ||
      !Number.isInteger(entry) ||
      entry < 0 ||
      entry > 6 ||
      days.includes(entry)
    ) {
      return DEFAULT_WORKING_HOURS.days;
    }
    days.push(entry);
  }
  return days;
}

/** Per-field working-hours validation: an invalid start or end degrades to its own default
 * independently; an end that does not get past the start first falls back to the default end
 * and, when a pathological start sits at or after that default end, the start comes down to
 * the default too - the end > start invariant always holds. */
function readWorkingHours(value: unknown): WorkingHoursPrefs {
  if (!isRecord(value)) {
    return DEFAULT_WORKING_HOURS;
  }
  let start =
    typeof value['start'] === 'string' && isWorkingTime(value['start'])
      ? value['start']
      : DEFAULT_WORKING_HOURS.start;
  let end =
    typeof value['end'] === 'string' && isWorkingTime(value['end'])
      ? value['end']
      : DEFAULT_WORKING_HOURS.end;
  if (end <= start) {
    end = DEFAULT_WORKING_HOURS.end;
    if (end <= start) {
      start = DEFAULT_WORKING_HOURS.start;
    }
  }
  return { start, end, days: readWorkingDays(value['days']) };
}

/** Pure load: unknown ids, non-strings, malformed JSON and missing keys fall back to the
 * defaults per field - one bad value never rejects the other. */
export function loadCalendarPrefs(storage: CalendarPrefsStorage): CalendarPrefs {
  try {
    const raw = storage.getItem(CALENDAR_PREFS_STORAGE_KEY);
    if (raw === null) {
      return { ...DEFAULT_CALENDAR_PREFS };
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return { ...DEFAULT_CALENDAR_PREFS };
    }
    return {
      startOfWeek: readStartOfWeek(parsed['startOfWeek']),
      defaultView: readDefaultView(parsed['defaultView']),
      workingHours: readWorkingHours(parsed['workingHours']),
    };
  } catch {
    return { ...DEFAULT_CALENDAR_PREFS };
  }
}

/** Pure save; storage failures (private mode quota) are swallowed - the preferences are cosmetic. */
export function saveCalendarPrefs(prefs: CalendarPrefs, storage: CalendarPrefsStorage): void {
  try {
    storage.setItem(CALENDAR_PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // cosmetic preferences: ignore write failures
  }
}

/** FullCalendar's firstDay number: monday→1, saturday→6, sunday→0. */
export function firstDayOf(startOfWeek: StartOfWeek): 0 | 1 | 6 {
  switch (startOfWeek) {
    case 'monday': {
      return 1;
    }
    case 'saturday': {
      return 6;
    }
    case 'sunday': {
      return 0;
    }
  }
}

/** The view a fresh /calendar opens on: the user's last chosen switcher view (vc.calendarView)
 * while it is still one of the five known views, otherwise the saved default view
 * (vc.calendarPrefs). A null storage (blocked localStorage) degrades to the built-in default. */
export function initialCalendarView(storage: CalendarPrefsStorage | null): ViewId {
  if (storage === null) {
    return DEFAULT_CALENDAR_PREFS.defaultView;
  }
  const savedView: unknown = storage.getItem(CALENDAR_VIEW_STORAGE_KEY);
  if (isKnownView(savedView)) {
    return savedView;
  }
  return loadCalendarPrefs(storage).defaultView;
}

/** The working-hours preferences into FullCalendar's `businessHours` option. FullCalendar
 * paints the INVERSE of the range: every time-grid cell outside it gets a dimming overlay
 * (class `fc-non-business`), so the working hours stand out as the undimmed band - that
 * inverse dimming IS the user-visible highlight. The grid keeps all 24 hours scrollable on
 * purpose (no slotMinTime/slotMaxTime). The days array is copied: the option is handed to a
 * third-party renderer that must not alias preference state. */
export function businessHoursPref(prefs: CalendarPrefs): BusinessHoursRange {
  return {
    daysOfWeek: [...prefs.workingHours.days],
    startTime: prefs.workingHours.start,
    endTime: prefs.workingHours.end,
  };
}
